import React from 'react'
import { Redirect } from 'react-router-dom'
import styled from 'styled-components'

import { Email, Form, Forms, PhoneNumber, useFormsContext } from '@ally/forms'
import { Box, Button, Media, TextBody } from '@ally/metronome-ui'
import { BadWords } from '@ally/use-validation'
import { GettingInSvg, LoginHeading } from '@ally/ally-login'

import { CIAMErrorBanner, CustomName, InputWrapper } from '../Shared'
import { useTransmit } from '../../TransmitContext'
import { GettingInMobileSvg } from '../svgs'

interface GuestEnrollFormInterface {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
}

const StyledTextBody = styled(TextBody)`
  margin-bottom: 24px;
`

const StyledBox = styled(Box)`
  ${({ theme: { media } }): string => `
    ${media.up('lg')} {
      display: flex;
      justify-content: space-between;
    }
  `}
`

const StyledFormWrapper = styled(Box)`
  ${({ theme: { media } }): string => `
    ${media.up('lg')} {
      width: ${(7 / 12) * 100}%; // based on a 12 column grid
    }
  `}
`

// svg right hand side
const StyledSvgWrapper = styled(Box)`
  width: ${(4 / 12) * 100}%; // based on a 12 column grid
  order: 1;
`

// svg left hand side, top
const StyledMobileSvgWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  flex: 1;
`

const StyledMobileSvg = styled(GettingInMobileSvg)`
  display: flex;
  width: 50%;
`

interface GuestEnrollCustomerFormProps {
  formId: string
}

export const GuestEnrollCustomerForm: React.FC<GuestEnrollCustomerFormProps> = ({
  formId,
}) => {
  const { hostServices } = useTransmit()
  const { isSubmitting } = useFormsContext()

  const badWords = hostServices?.hostData.badWords.data
  const badWordsObject: BadWords = badWords || {
    base: [],
    names: [],
    substring: [],
  }

  return (
    <Form id={formId}>
      <InputWrapper mb="md">
        <CustomName
          badWordsObject={badWordsObject}
          variant="First Name"
          name="firstName"
          data-allytmfn="firstName"
          required
          data-testid="guest-enroll-first-name"
        />
      </InputWrapper>

      <InputWrapper mb="md">
        <CustomName
          badWordsObject={badWordsObject}
          variant="Last Name"
          name="lastName"
          data-allytmfn="lastName"
          required
          data-testid="guest-enroll-last-name"
        />
      </InputWrapper>

      <InputWrapper mb="md">
        <Email
          data-testid="guest-enroll-email"
          name="email"
          data-allytmfn="email"
          required
        />
      </InputWrapper>

      <InputWrapper mb="md">
        <PhoneNumber
          data-testid="guest-enroll-phone"
          name="phoneNumber"
          label="Phone number"
          data-allytmfn="phoneNumber"
          required
        />
      </InputWrapper>
      <StyledTextBody
        tag="p"
        size="sm"
        data-testid="guest-enroll-phone-sub-copy"
      >
        We&apos;ll use this number to send you security codes.
      </StyledTextBody>

      <StyledTextBody
        tag="p"
        size="sm"
        data-testid="guest-enroll-form-disclaimer"
      >
        You authorize us to contact you using any telephone number you provide
        to us, including a mobile number that you are authorized to use. In
        addition to manual calling, we may use text messages, prerecorded or
        artificial voice messages, or automatic dialing systems. We will not
        charge you for any contact but your phone service provider may.
      </StyledTextBody>

      <Button
        text="Continue"
        type="submit"
        variant="primary"
        fullWidthMd
        loading={isSubmitting}
        allytmln="continueGuestEnrollment"
      />
    </Form>
  )
}

interface GuestEnrollCustomerContentProps {
  error: string | undefined
}

export const GuestEnrollCustomerContent: React.FC<GuestEnrollCustomerContentProps> = ({
  error,
}) => {
  const { hostServices } = useTransmit()
  const { actions } = useTransmit()

  React.useEffect(() => {
    hostServices?.analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Login:Guest Enrollment',
    })
  }, [])

  const handleSubmit = (formValues: GuestEnrollFormInterface): Promise<any> => {
    return actions.enrollGuest({
      payload: formValues,
      journey_version: 'v2',
    })
  }

  const formId = 'guestEnrollCustomer'

  const initialValues: GuestEnrollFormInterface = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  }

  return (
    <StyledBox data-testid="guest-login-page-wrapper">
      <StyledFormWrapper data-testid="guest-login-form-wrapper">
        <LoginHeading title="Introduce yourself." />
        <Media.MdDown>
          <StyledMobileSvgWrapper>
            <StyledMobileSvg />
          </StyledMobileSvgWrapper>
        </Media.MdDown>

        <StyledTextBody tag="p" size="sm" data-testid="guest-enroll-sub-header">
          Let&apos;s get to know each other. Quick tip: use your legal name here
          to make the most of Ally&apos;s features.
        </StyledTextBody>

        <CIAMErrorBanner error={error} />

        <Forms
          formId={formId}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          <GuestEnrollCustomerForm formId={formId} />
        </Forms>
      </StyledFormWrapper>
      <StyledSvgWrapper data-testid="getting-in-image">
        <Media.LgUp>
          <GettingInSvg />
        </Media.LgUp>
      </StyledSvgWrapper>
    </StyledBox>
  )
}

interface GuestEnrollCustomerProps {
  isGuestEnabled: boolean
}

export const GuestEnrollCustomer: React.FC<GuestEnrollCustomerProps> = ({
  isGuestEnabled,
}) => {
  const { activeScreen } = useTransmit()

  if (!isGuestEnabled) {
    return <Redirect to="/" />
  }

  return <GuestEnrollCustomerContent error={activeScreen?.payload?.error} />
}
